import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import DashboardShell from "@/components/nav/DashboardShell";
import { toast } from "sonner";
import { Pause, Play, Send, ShieldCheck, Loader2 } from "lucide-react";

export default function EmployeeCommand() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [perms, setPerms] = useState([]);
  const [busy, setBusy] = useState(false);
  const [assign, setAssign] = useState({ description: "", priority: "normal", require_approval: false });

  const load = async () => {
    const { data: detail } = await api.get(`/customer-employees/${id}`);
    setData(detail);
    const [{ data: t }, { data: p }] = await Promise.all([
      api.get(`/tasks?customer_employee_id=${id}&limit=20`),
      api.get(`/customer-employees/${id}/permissions`),
    ]);
    setTasks(t); setPerms(p);
  };

  useEffect(() => { load().catch(err => toast.error(err?.response?.data?.detail || "Failed to load")); }, [id]);

  const toggle = async (action) => {
    await api.post(`/customer-employees/${id}/${action}`);
    load();
  };

  const submitTask = async (e) => {
    e.preventDefault();
    if (!assign.description.trim()) return;
    setBusy(true);
    try {
      await api.post("/tasks/assign", { customer_employee_id: id, ...assign });
      setAssign({ description: "", priority: "normal", require_approval: false });
      toast.success("Task assigned");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to assign task");
    } finally { setBusy(false); }
  };

  const updatePerm = async (task_key, mode) => {
    setPerms(perms.map(p => p.task_key === task_key ? {...p, mode} : p));
    await api.post(`/customer-employees/${id}/permissions`, { task_key, mode });
  };

  if (!data) return <DashboardShell><div className="text-smoke flex items-center gap-3"><Loader2 className="animate-spin" size={16} /> Loading…</div></DashboardShell>;

  const ce = data.employee, cat = data.catalog;

  return (
    <DashboardShell>
      <div className="flex items-start justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="brand-eyebrow mb-3">{cat.industry?.toUpperCase()}</div>
          <h1 className="brand-headline text-5xl md:text-7xl">{ce.display_name}<span className="text-gold">.</span></h1>
          <div className="mt-3 flex items-center gap-3 text-smoke text-sm">
            <span className={`dot ${ce.status==='Working'?'dot-green':ce.status==='Paused'?'dot-gray':'dot-amber'}`} />
            {ce.status}
          </div>
        </div>
        <div className="flex gap-3">
          {ce.status === "Paused" ? (
            <button data-testid="emp-resume" onClick={() => toggle("resume")} className="btn-gold text-sm px-5 py-3"><Play size={14} /> Resume</button>
          ) : (
            <button data-testid="emp-pause" onClick={() => toggle("pause")} className="btn-outline text-sm px-5 py-3"><Pause size={14} /> Pause</button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Assign task */}
        <div className="lg:col-span-2 card-white p-8">
          <div className="brand-eyebrow" style={{color:'#0B0D10'}}>ASSIGN A TASK</div>
          <div className="brand-headline text-2xl mt-2 text-ink">Tell {ce.display_name} what to do.</div>
          <form onSubmit={submitTask} className="mt-6 space-y-4">
            <textarea data-testid="assign-desc" required rows="4" placeholder={`e.g. Draft a friendly reminder to residents about the pool closing this weekend.`}
              value={assign.description} onChange={e => setAssign({...assign, description: e.target.value})}
              style={{background:'#FFFFFF', color:'#0B0D10', border:'1px solid #d4d4d4'}} />
            <div className="flex flex-wrap items-center gap-4">
              <div>
                <label style={{color:'#0B0D10'}}>Priority</label>
                <select data-testid="assign-priority" value={assign.priority} onChange={e => setAssign({...assign, priority: e.target.value})}
                  style={{background:'#FFFFFF', color:'#0B0D10', border:'1px solid #d4d4d4'}}>
                  <option value="low">Low</option>
                  <option value="normal">Normal</option>
                  <option value="high">High</option>
                </select>
              </div>
              <label className="flex items-center gap-2 text-ink text-sm" style={{textTransform:'none', letterSpacing:'normal', fontWeight:400, color:'#0B0D10'}}>
                <input data-testid="assign-approval" type="checkbox" style={{width:'18px', height:'18px'}} checked={assign.require_approval} onChange={e => setAssign({...assign, require_approval: e.target.checked})} />
                Require approval before delivery
              </label>
            </div>
            <button data-testid="assign-submit" type="submit" disabled={busy} className="bg-ink text-white px-6 py-3 font-bold hover:bg-gold hover:text-ink transition-colors inline-flex items-center gap-2">
              {busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
              {busy ? "Working…" : "Assign Task"}
            </button>
          </form>
        </div>

        <div className="border border-line p-6">
          <div className="brand-eyebrow">DEFAULT PERMISSIONS</div>
          <div className="mt-4 divide-y divide-line">
            {perms.slice(0, 5).map(p => (
              <div key={p.task_key} className="py-3">
                <div className="text-white text-sm">{p.task_label}</div>
                <div className="mt-1.5 flex gap-2">
                  {["AUTO","APPROVAL REQUIRED","DISABLED"].map(m => (
                    <button key={m} data-testid={`perm-${p.task_key}-${m.split(' ')[0].toLowerCase()}`}
                      onClick={() => updatePerm(p.task_key, m)}
                      className={`text-[9px] tracking-widest font-bold px-2 py-1 border ${p.mode===m ? "bg-gold text-ink border-gold" : "text-smoke border-line hover:border-gold"}`}>{m}</button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-10">
        <div className="brand-eyebrow mb-4">RECENT TASKS</div>
        <div className="border border-line divide-y divide-line">
          {tasks.length === 0 && <div className="p-8 text-smoke text-sm">No tasks yet. Assign one above.</div>}
          {tasks.map(t => (
            <div key={t.id} className="p-5">
              <div className="flex items-center justify-between gap-4">
                <div className="text-white text-sm">{t.description}</div>
                <span className={`text-[10px] tracking-widest font-bold ${t.status==='COMPLETED'?'text-emerald-400':t.status==='FAILED'?'text-red-400':t.status.startsWith('WAITING')?'text-amber-400':'text-smoke'}`}>{t.status}</span>
              </div>
              {t.ai_output && <div className="mt-3 text-smoke text-sm whitespace-pre-wrap bg-white/[0.02] p-4 border border-line">{t.ai_output}</div>}
              {t.error && <div className="mt-3 text-red-400 text-sm">{t.error}</div>}
              <div className="mt-2 text-[11px] text-smoke">{new Date(t.created_at).toLocaleString()}</div>
            </div>
          ))}
        </div>
      </div>
    </DashboardShell>
  );
}
